import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import dashboardService from '../services/dashboardService';
import EventTutorialModal from './EventTutorialModal';

// Icons as inline SVGs for zero dependencies
const Icons = {
  Calendar: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
    </svg>
  ),
  Ticket: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
    </svg>
  ),
  ClipboardList: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
    </svg>
  ),
  ChartBar: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
  Shield: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  ),
  Plus: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  ),
  User: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  ),
  Cog: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  Logout: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
    </svg>
  ),
  QuestionMark: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
    </svg>
  ),
  Bell: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
    </svg>
  ),
  Menu: () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  ),
  X: () => (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  ChevronDown: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  ),
};

// Role-based accent colors
const ROLE_COLORS = {
  attendee: { main: '#0288d1', dark: '#0277bd', light: '#e1f5fe', text: '#01579b' },
  organizer: { main: '#e94560', dark: '#d63751', light: '#fef2f3', text: '#b42a42' },
  admin:     { main: '#7c3aed', dark: '#6d28d9', light: '#f5f3ff', text: '#5b21b6' },
};

function getAccentColors(role) {
  return ROLE_COLORS[role] || ROLE_COLORS.attendee;
}

// Navigation items based on role
const getNavItems = (role, isAuthenticated, isPresenter) => {
  const publicItems = [
    { label: 'Browse Events', path: '/events', icon: Icons.Calendar },
  ];

  if (!isAuthenticated) {
    return publicItems;
  }

  const attendeeItems = [
    { label: 'Browse Events', path: '/events', icon: Icons.Calendar },
    { label: 'My Schedule', path: '/my-events', icon: Icons.Ticket },
    ...(isPresenter ? [{ label: 'Presenter View', path: '/dashboard', icon: Icons.ClipboardList }] : []),
  ];

  const organizerItems = [
    { label: 'Browse Events', path: '/events', icon: Icons.Calendar },
    { label: 'My Schedule', path: '/my-events', icon: Icons.Ticket },
    { label: 'Presenter View', path: '/dashboard', icon: Icons.ClipboardList },
    { label: 'Organizer Dashboard', path: '/organizer', icon: Icons.ChartBar },
  ];

  const adminItems = [
    ...organizerItems,
    { label: 'Admin Panel', path: '/admin', icon: Icons.Shield },
  ];

  switch (role) {
    case 'admin':
      return adminItems;
    case 'organizer':
      return organizerItems;
    default:
      return attendeeItems;
  }
};

// User avatar component
function UserAvatar({ user, size = 'md' }) {
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
  };

  const initials = user?.first_name && user?.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    : user?.email?.[0]?.toUpperCase() || 'U';

  const colors = getAccentColors(user?.role);

  return (
    <div
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center text-white font-semibold shadow-sm`}
      style={{ background: `linear-gradient(to bottom right, ${colors.main}, ${colors.dark})` }}
    >
      {initials}
    </div>
  );
}

// User dropdown menu
function UserDropdown({ user, onLogout }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    await onLogout();
    navigate('/login');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <UserAvatar user={user} size="sm" />
        <div className="hidden sm:block text-left">
          <div className="text-sm font-medium text-brand-dark truncate max-w-[120px]">
            {user?.first_name || 'User'}
          </div>
        </div>
        <Icons.ChevronDown />
      </button>

      {/* Dropdown menu */}
      <div
        className={`absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 transform transition-all duration-200 origin-top-right ${
          isOpen
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
        }`}
        role="menu"
        aria-orientation="vertical"
      >
        {/* User info header */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <UserAvatar user={user} size="md" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-brand-dark truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-brand-muted truncate">{user?.email}</p>
              <span
                className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full capitalize"
                style={{ backgroundColor: getAccentColors(user?.role).light, color: getAccentColors(user?.role).text }}
              >
                {user?.role || 'attendee'}
              </span>
            </div>
          </div>
        </div>

        {/* Menu items */}
        <div className="py-1">
          <Link
            to="/settings"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            role="menuitem"
          >
            <Icons.User />
            <span>Profile</span>
          </Link>
          <Link
            to="/settings/notifications"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            role="menuitem"
          >
            <Icons.Cog />
            <span>Settings</span>
          </Link>
          <button
            onClick={() => { setIsOpen(false); setShowTutorial(true); }}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            role="menuitem"
          >
            <Icons.QuestionMark />
            <span>Help Guide</span>
          </button>
        </div>

        {/* Logout */}
        <div className="border-t border-gray-100 pt-1">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            role="menuitem"
          >
            <Icons.Logout />
            <span>Sign out</span>
          </button>
        </div>
      </div>

      {showTutorial && (
        <EventTutorialModal onDismiss={() => setShowTutorial(false)} />
      )}
    </div>
  );
}

// Mobile menu component
function MobileMenu({ isOpen, onClose, navItems, user, isAuthenticated, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showTutorial, setShowTutorial] = useState(false);

  const handleLogout = async () => {
    onClose();
    await onLogout();
    navigate('/login');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity duration-300 md:hidden ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Mobile menu panel */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-72 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-out md:hidden ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <span className="font-serif text-xl text-brand-dark">Menu</span>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-brand-dark hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <Icons.X />
          </button>
        </div>

        {/* User info (if logged in) */}
        {isAuthenticated && user && (
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <UserAvatar user={user} size="md" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-brand-dark truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-brand-muted truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation items */}
        <nav className="p-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const colors = getAccentColors(user?.role);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                style={isActive ? { backgroundColor: colors.light, color: colors.text } : undefined}
              >
                <Icon />
                <span>{item.label}</span>
              </Link>
            );
          })}

          {/* Create Event button for organizers/admins */}
          {isAuthenticated && (user?.role === 'organizer' || user?.role === 'admin') && (
            <Link
              to="/events/create"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 mt-2 text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: getAccentColors(user?.role).main }}
            >
              <Icons.Plus />
              <span>Create Event</span>
            </Link>
          )}
        </nav>

        {/* Bottom section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-white">
          {isAuthenticated ? (
            <div className="space-y-2">
              <Link
                to="/settings"
                onClick={onClose}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Icons.Cog />
                <span>Settings</span>
              </Link>
              <button
                onClick={() => { onClose(); setShowTutorial(true); }}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Icons.QuestionMark />
                <span>Help Guide</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Icons.Logout />
                <span>Sign out</span>
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <Link
                to="/login"
                onClick={onClose}
                className="block w-full px-4 py-2.5 text-center text-sm font-medium text-brand-dark border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                onClick={onClose}
                className="block w-full px-4 py-2.5 text-center text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: getAccentColors(user?.role).main }}
              >
                Get started
              </Link>
            </div>
          )}
        </div>
      </div>

      {showTutorial && (
        <EventTutorialModal onDismiss={() => setShowTutorial(false)} />
      )}
    </>
  );
}

// Main Navigation component
export default function Navigation() {
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isPresenter, setIsPresenter] = useState(false);
  const location = useLocation();

  // Check presenter status for attendees
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setIsPresenter(false);
      return;
    }
    // Organizers/admins always see Presenter View
    if (user.role === 'organizer' || user.role === 'admin') {
      setIsPresenter(true);
      return;
    }
    let cancelled = false;
    dashboardService.getPresenterDashboard()
      .then((result) => {
        if (cancelled) return;
        const events = result.data?.events || [];
        setIsPresenter(events.some((e) => e.sessions && e.sessions.length > 0));
      })
      .catch(() => {
        if (!cancelled) setIsPresenter(false);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id, user?.role]);

  const navItems = getNavItems(user?.role, isAuthenticated, isPresenter);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              to="/events"
              className="flex items-center gap-2 group"
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow"
                style={{ background: `linear-gradient(to bottom right, ${getAccentColors(user?.role).main}, ${getAccentColors(user?.role).dark})` }}
              >
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="font-serif text-xl text-brand-dark transition-colors">
                rSchdlr
              </span>
            </Link>

            {/* Desktop navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const colors = getAccentColors(user?.role);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? ''
                        : 'text-gray-600 hover:text-brand-dark hover:bg-gray-50'
                    }`}
                    style={isActive ? { backgroundColor: colors.light, color: colors.text } : undefined}
                  >
                    <Icon />
                    <span>{item.label}</span>
                  </Link>
                );
              })}

              {/* Create Event button for organizers/admins */}
              {isAuthenticated && (user?.role === 'organizer' || user?.role === 'admin') && (
                <Link
                  to="/events/create"
                  className="flex items-center gap-1.5 ml-2 px-4 py-2 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow hover:opacity-90 transition-all"
                  style={{ backgroundColor: getAccentColors(user?.role).main }}
                >
                  <Icons.Plus />
                  <span>Create Event</span>
                </Link>
              )}
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {isAuthenticated ? (
                <>
                  {/* Notification bell */}
                  <NotificationBell />

                  {/* User dropdown (hidden on mobile) */}
                  <div className="hidden md:block">
                    <UserDropdown user={user} onLogout={logout} />
                  </div>

                  {/* Mobile menu button */}
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="md:hidden p-2 text-gray-500 hover:text-brand-dark hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Open menu"
                  >
                    <Icons.Menu />
                  </button>
                </>
              ) : (
                <>
                  {/* Auth buttons for desktop */}
                  <div className="hidden md:flex items-center gap-2">
                    <Link
                      to="/login"
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-brand-dark transition-colors"
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-2 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow hover:opacity-90 transition-all"
                      style={{ backgroundColor: getAccentColors(user?.role).main }}
                    >
                      Get started
                    </Link>
                  </div>

                  {/* Mobile menu button */}
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="md:hidden p-2 text-gray-500 hover:text-brand-dark hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Open menu"
                  >
                    <Icons.Menu />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        navItems={navItems}
        user={user}
        isAuthenticated={isAuthenticated}
        onLogout={logout}
      />
    </>
  );
}
